import { formatISO9075 } from 'date-fns';
import { getDocumentAsync } from 'expo-document-picker';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';
import {
  ImagePickerResult,
  launchCameraAsync,
  launchImageLibraryAsync,
} from 'expo-image-picker';
import { UIImagePickerPresentationStyle } from 'expo-image-picker/build/ImagePicker.types';
import { getImageSize } from 'lib/getImageSize';
import { Platform } from 'react-native';
import {
  FILE_TYPES,
  MAX_FILE_SIZE,
  FileBeingAdded,
  FileType,
  FileTypeExtensions,
} from 'service/file/types';
import { hapticSelection } from 'lib/withHapticFeedback';
import * as MediaLibrary from 'expo-media-library';
import { File as ExpoFile } from 'expo-file-system';

export const getDateFormattedForFilename = (): string =>
  formatISO9075(new Date(), { format: 'basic' });

export async function returnFileAsBase64({
  file,
  resizeConfig,
}: {
  file: FileBeingAdded;
  resizeConfig?: {
    width: number;
    height: number;
    maxWidth: number;
    maxHeight: number;
    compression: number;
  };
}): Promise<string> {
  const uri = file.uri || (await readAsDataURLAsync(file.file!));
  if (resizeConfig) {
    return (
      await resizeImage({
        uri,
        width: resizeConfig.width,
        height: resizeConfig.height,
        maxWidth: resizeConfig.maxWidth,
        maxHeight: resizeConfig.maxHeight,
        compression: resizeConfig.compression,
      })
    ).base64;
  }
  return Platform.OS === 'web'
    ? removeDataBase64Prefix(uri)
    : await new ExpoFile(uri).base64();
}

export const readAsDataURLAsync = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const getFileTypeFromUntrustedExtension = (
  extension: FileTypeExtensions | string,
): FileType | undefined => FILE_TYPES[extension as FileTypeExtensions];

const mbToBytes = (mb: number) => 2 ** 20 * mb;

export const validateFileSize = (
  file: FileBeingAdded,
  maxFileSizeMb?: number,
): boolean => {
  if (
    !file.size ||
    file.size > (maxFileSizeMb ? mbToBytes(maxFileSizeMb) : MAX_FILE_SIZE)
  ) {
    return false;
  }
  return true;
};

export const removeDataBase64Prefix = (value: string): string =>
  value.replace(/^((data:)[a-z]+\/[a-z-.+]+(;base64,))/, '');

export const getDocument: (options?: {
  type?: string;
  multiple?: boolean;
}) => Promise<FileBeingAdded[]> = async (options) => {
  if (Platform.OS === 'web') {
    const result = await getDocumentAsync({
      copyToCacheDirectory: true,
      multiple: !!options?.multiple,
      type: options?.type ?? '*/*',
    });
    const validatedFiles: FileBeingAdded[] = [];
    if (result.assets) {
      const outputLength = result.assets.length;
      for (let i = 0; i < outputLength; i += 1) {
        const file = result.assets[i];
        if (file?.file && file.size !== undefined) {
          validatedFiles.push({
            file: file.file,
            uri: undefined,
            name: file.name,
            size: file.size,
            width: 0,
            height: 0,
          });
        }
      }
    }
    return validatedFiles;
  }
  const result = await getDocumentAsync({
    copyToCacheDirectory: true,
    type: options?.type ?? '*/*',
    multiple: false,
  });
  const [asset] = result?.assets || [];
  if (!asset) {
    return [];
  }
  return [
    {
      file: undefined,
      uri: asset.uri,
      size: asset.size || 1,
      name: asset.name,
      width: 0,
      height: 0,
    },
  ];
};

export const getImage = async (options?: {
  multiple?: boolean;
}): Promise<FileBeingAdded[]> => {
  // Web
  if (Platform.OS === 'web') {
    const result = await getDocumentAsync({
      copyToCacheDirectory: true,
      multiple: !!options?.multiple,
      type: 'image/*',
    });
    const validatedFiles: FileBeingAdded[] = [];
    if (result.assets) {
      const outputLength = result.assets.length;
      for (let i = 0; i < outputLength; i += 1) {
        const file = result.assets[i];
        if (file?.file && file.size !== undefined) {
          validatedFiles.push({
            file: file.file,
            uri: undefined,
            name: file.name,
            size: file.size,
            width: 0,
            height: 0,
          });
        }
      }
    }
    return validatedFiles;
  }
  // Native
  const result: ImagePickerResult = await launchImageLibraryAsync({
    // quality: 0.4,
    allowsEditing: false,
    allowsMultipleSelection: !!options?.multiple,
  });
  if (result.canceled) {
    return [];
  }
  return result.assets.map((asset, index) => ({
    file: undefined,
    uri: asset.uri,
    name: `Image - ${getDateFormattedForFilename()}${
      result.assets.length ? ` - ${index}` : ''
    }.jpeg`,
    width: asset.width,
    height: asset.height,
    size: asset.fileSize || 0,
  }));
};

export enum GetPhotoErrors {
  cannotSaveBackup = 'cannotSaveBackup',
}

export const getPhoto = async ({
  saveToMediaLibrary,
}: {
  saveToMediaLibrary: boolean;
}): Promise<FileBeingAdded[]> => {
  // No camera option available for web
  if (Platform.OS === 'web') {
    return [];
  }
  hapticSelection();
  const result = await launchCameraAsync({
    // quality: 0.4,
    allowsEditing: false,
    presentationStyle: UIImagePickerPresentationStyle.OVER_FULL_SCREEN,
  });
  if (result.canceled) {
    return [];
  }
  if (saveToMediaLibrary) {
    try {
      await Promise.all(
        result.assets.map((asset) =>
          MediaLibrary.saveToLibraryAsync(asset.uri),
        ),
      );
    } catch (_) {
      throw new Error(GetPhotoErrors.cannotSaveBackup);
    }
  }
  // Images get resized so we don't need the base64 version of them
  return result.assets.map((asset, index) => ({
    file: undefined,
    uri: asset.uri,
    name: `Image - ${getDateFormattedForFilename()}${
      result.assets.length ? ` - ${index}` : ''
    }.jpeg`,
    width: asset.width,
    height: asset.height,
    size: 0,
  }));
};

export const resizeImage = async ({
  uri,
  width: inputWidth,
  height: inputHeight,
  maxWidth,
  maxHeight,
  compression,
}: {
  uri: string;
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
  compression: number;
}): Promise<{ base64: string; width: number; height: number }> => {
  const { height, width } =
    inputWidth && inputHeight
      ? { height: inputHeight, width: inputWidth }
      : await getImageSize(uri);

  const resize =
    width <= maxWidth && height <= maxHeight
      ? { height, width }
      : width > height
        ? { width: maxWidth }
        : { height: maxHeight };

  const imageRef = await ImageManipulator.manipulate(uri)
    .resize(resize)
    .renderAsync();

  const imageResult = await imageRef.saveAsync(
    compression
      ? {
          compress: compression / 100,
          format: SaveFormat.JPEG,
          base64: true,
        }
      : { compress: 1, format: SaveFormat.JPEG, base64: true },
  );

  return {
    base64: imageResult.base64!,
    width: imageResult.width,
    height: imageResult.height,
  };
};
